import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaBriefcase, FaCalendarAlt, FaComment, FaPlus, FaSearch, FaTable } from "react-icons/fa";
import Layout from "../../../components/Layout";
import MonthCalendar from "../../../components/MonthCalendar";
import { crm } from "../../../theme";
import { CommercialClient, SHARED_INTRANET_FIELDS, commercialStatuses } from "../../../models/CommercialClient";
import { COMPANY_OPERATIONS, COMPANY_TYPE_OPTIONS, LOGISTICS_AND_DISTRIBUTION_TYPES, companyTypeLabel } from "../../../models/Company";
import {
  addCommercialClientComment,
  getCommercialClient,
  getCommercialClients,
  saveCommercialClient,
  updateCommercialClient,
  updateCommercialClientStatus,
} from "../../../services/commercialClientService";
import { getCompany } from "../../../services/companyService";
import BranchesEditor from "./BranchesEditor";
import ClientBillingSettings from "./ClientBillingSettings";
import ProceduresPanel from "../../intranet/ProceduresPanel";

const statusColors: Record<string, string> = {
  Prospect: "gray",
  Contato: "blue",
  "Em negociação": "orange",
  "Proposta enviada": "purple",
  "Contrato fechado": "green",
  Perdido: "red",
};

const percent = (value?: number | null) =>
  value === null || value === undefined ? "—" : `${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const dateText = (value?: string | null) =>
  value ? new Date(`${value.substring(0, 10)}T12:00:00`).toLocaleDateString("pt-BR") : "—";

const dateInput = (value?: string | null) => value ? value.substring(0, 10) : "";

const emptyClient: CommercialClient = { status: "Prospect", comments: [] };

// "Novo" só conta como editado se algum campo (além do status padrão) recebeu valor.
const hasValues = (client: CommercialClient) =>
  Object.entries(client).some(([key, value]) => key !== "status" && key !== "comments" && value !== "" && value != null);

const UNIT_FILTER_OPTIONS: [number | null, string][] = [
  [null, "Todos"],
  ...COMPANY_TYPE_OPTIONS.filter((o) => LOGISTICS_AND_DISTRIBUTION_TYPES.includes(o.value)).map((o): [number, string] => [o.value, o.label]),
];

const CommercialClients = () => {
  const [clients, setClients] = useState<CommercialClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [unitFilter, setUnitFilter] = useState<number | null>(null);
  const [operationFilter, setOperationFilter] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState<CommercialClient | null>(null);
  const [draft, setDraft] = useState<CommercialClient>(emptyClient);
  const [newClient, setNewClient] = useState<CommercialClient>(emptyClient);
  const [comment, setComment] = useState("");
  const [proceduresDirty, setProceduresDirty] = useState(false);
  const detail = useDisclosure();
  const create = useDisclosure();
  const confirmDiscard = useDisclosure();
  const [discardTarget, setDiscardTarget] = useState<"detail" | "create">("detail");
  const keepEditingRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    try {
      setClients(await getCommercialClients({ search: search || undefined, status: status || undefined }));
    } catch {
      toast({ title: "Não foi possível carregar os clientes ativos.", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status]);

  // ?companyId=… (atalho do detalhe do processo): abre o cadastro do cliente, ou o "Novo" já vinculado à empresa.
  const location = useLocation();
  const navigate = useNavigate();
  const linkedCompanyId = new URLSearchParams(location.search).get("companyId");
  useEffect(() => {
    if (!linkedCompanyId || loading) return;
    navigate(location.pathname, { replace: true });
    const match = clients.find((client) => client.companyId === linkedCompanyId);
    if (match) {
      openDetail(match);
      return;
    }
    getCompany(linkedCompanyId)
      .then((company) => {
        setNewClient({ ...emptyClient, companyId: linkedCompanyId, newCompanyName: company?.name ?? "", newCompanyCnpj: company?.cnpj ?? "" });
        create.onOpen();
        toast({ title: "Cliente ainda sem cadastro comercial", description: "Crie o cadastro para acompanhar no Clientes Ativos.", status: "info" });
      })
      .catch(() => toast({ title: "Não foi possível abrir o cliente.", status: "error" }));
  }, [linkedCompanyId, loading]);

  const filtered = useMemo(() => {
    const value = search.trim().toLowerCase();
    return clients.filter((client) =>
      (unitFilter == null || client.companyType === unitFilter)
      && (!operationFilter || client.companyOperation === operationFilter)
      && (!value || [client.name, client.cnpj, client.responsible, client.analyst, client.agent1, client.agent2]
        .some((field) => field?.toLowerCase().includes(value))));
  }, [clients, search, unitFilter, operationFilter]);

  const openDetail = async (client: CommercialClient) => {
    try {
      const full = await getCommercialClient(client.id!);
      setSelected(full);
      setDraft(full);
      detail.onOpen();
    } catch {
      toast({ title: "Não foi possível abrir o cadastro.", status: "error" });
    }
  };

  const saveDetail = async () => {
    if (!draft.id) return;
    if (!draft.name?.trim()) {
      toast({ title: "Informe o nome do cliente.", status: "warning" });
      return;
    }
    try {
      // Campos também editados na Intranet: só vão se mudaram aqui (null = servidor mantém o atual)
      const payload: Record<string, unknown> = { ...draft };
      SHARED_INTRANET_FIELDS.forEach((key) => {
        if (selected && (draft[key] ?? "") === (selected[key] ?? "")) payload[key] = null;
      });
      const { data } = await updateCommercialClient(payload as CommercialClient);
      if (data && data.success === false) {
        toast({ title: data.notifications?.[0] ?? "Não foi possível salvar o cadastro.", status: "error" });
        return;
      }
      toast({ title: "Cadastro comercial atualizado.", status: "success" });
      await load();
      const saved = await getCommercialClient(draft.id);
      setSelected(saved);
      setDraft(saved);
    } catch {
      toast({ title: "Não foi possível salvar o cadastro.", status: "error" });
    }
  };

  const createClient = async () => {
    if (!newClient.newCompanyName?.trim()) {
      toast({ title: "Informe o nome do cliente.", status: "warning" });
      return;
    }
    try {
      const { data } = await saveCommercialClient(newClient);
      if (data && data.success === false) {
        toast({ title: data.notifications?.[0] ?? "Não foi possível criar o cadastro.", status: "error" });
        return;
      }
      toast({ title: "Cliente incluído no cadastro comercial.", status: "success" });
      create.onClose();
      setNewClient(emptyClient);
      await load();
    } catch {
      toast({ title: "Não foi possível criar o cadastro.", status: "error" });
    }
  };

  const saveComment = async () => {
    if (!selected?.id || !comment.trim()) return;
    try {
      await addCommercialClientComment(selected.id, comment);
      const full = await getCommercialClient(selected.id);
      setSelected(full);
      setDraft((current) => ({ ...current, comments: full.comments }));
      setComment("");
    } catch {
      toast({ title: "Não foi possível registrar o comentário.", status: "error" });
    }
  };

  const moveStatus = async (client: CommercialClient, nextStatus: string) => {
    try {
      await updateCommercialClientStatus(client.id!, nextStatus);
      await load();
      if (selected?.id === client.id) {
        const full = await getCommercialClient(client.id!);
        setSelected(full);
        setDraft((current) => ({ ...current, status: full.status }));
      }
    } catch {
      toast({ title: "Não foi possível alterar o status.", status: "error" });
    }
  };

  // Fechar com edição não salva pede confirmação; sem alterações fecha direto.
  const askDiscard = (target: "detail" | "create") => { setDiscardTarget(target); confirmDiscard.onOpen(); };
  const requestClose = () => (selected && (proceduresDirty || JSON.stringify(draft) !== JSON.stringify(selected)) ? askDiscard("detail") : detail.onClose());
  const requestCloseCreate = () => (hasValues(newClient) ? askDiscard("create") : create.onClose());
  const discardChanges = () => {
    confirmDiscard.onClose();
    if (discardTarget === "create") { create.onClose(); setNewClient(emptyClient); } else detail.onClose();
  };

  const grouped = useMemo(() => commercialStatuses.map((name) => ({ name, clients: filtered.filter((client) => client.status === name) })), [filtered]);
  return (
    <Box minH="100vh" bg={crm.pageBg} color={crm.text}>
      <Layout>
        <Box minH="100%" w="100%" bg={crm.pageBg} overflowY="auto" p={{ base: "18px", md: "24px" }}>
          <Box pb="36px">
            <Flex align="center" gap="12px" mb="22px">
              <Box w="4px" h="32px" bg={crm.brand} borderRadius="full" />
              <Heading fontSize="28px" color={crm.text}>Clientes Ativos</Heading>
            </Flex>
            <Tabs variant="unstyled" size="sm" w="100%" minW="0">
              <Flex justify="space-between" align="center" gap="14px" wrap="wrap">
                <TabList bg={crm.subtle} p="4px" borderRadius="10px" display="inline-flex" gap="2px" border="1px solid" borderColor={crm.border} flexWrap="wrap">
                  <Tab borderRadius="8px" color={crm.textMuted} _selected={{ bg: crm.surface, color: crm.brand, boxShadow: crm.shadowSm }}><FaTable style={{ marginRight: 7 }} />Tabela Geral</Tab>
                  <Tab borderRadius="8px" color={crm.textMuted} _selected={{ bg: crm.surface, color: crm.brand, boxShadow: crm.shadowSm }}><FaBriefcase style={{ marginRight: 7 }} />Pipeline de Vendas</Tab>
                  <Tab borderRadius="8px" color={crm.textMuted} _selected={{ bg: crm.surface, color: crm.brand, boxShadow: crm.shadowSm }}><FaCalendarAlt style={{ marginRight: 7 }} />Calendário</Tab>
                </TabList>
                <Flex gap="9px" align="center" flexWrap="wrap">
                  <ButtonGroup size="sm" isAttached variant="outline">
                    {UNIT_FILTER_OPTIONS.map(([value, label]) => (
                      <Button key={label} aria-pressed={unitFilter === value} onClick={() => setUnitFilter(value)} bg={unitFilter === value ? crm.brand : crm.surface} color={unitFilter === value ? "white" : crm.textMuted} borderColor={crm.borderStrong} _hover={{ bg: unitFilter === value ? crm.brandDark : undefined }}>{label}</Button>
                    ))}
                  </ButtonGroup>
                  <Tooltip label="Filtrar por tipo de operação"><Select value={operationFilter} onChange={(event) => setOperationFilter(event.target.value)} w="170px" size="sm" bg={crm.surface} borderColor={crm.borderStrong}><option value="">Todas as operações</option>{COMPANY_OPERATIONS.map((item) => <option key={item} value={item}>{item}</option>)}</Select></Tooltip>
                  {(unitFilter != null || operationFilter) && <Button size="sm" variant="ghost" onClick={() => { setUnitFilter(null); setOperationFilter(""); }}>Limpar empresa/operação</Button>}
                  <Tooltip label="Filtrar por status"><Select value={status} onChange={(event) => setStatus(event.target.value)} w="170px" size="sm" bg={crm.surface} borderColor={crm.borderStrong}><option value="">Todos os status</option>{commercialStatuses.map((item) => <option key={item} value={item}>{item}</option>)}</Select></Tooltip>
                  <Box position="relative"><FaSearch style={{ position: "absolute", left: 10, top: 9, color: crm.textMuted, zIndex: 1 }} /><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar" size="sm" w="180px" pl="31px" bg={crm.surface} borderColor={crm.borderStrong} /></Box>
                  <Button leftIcon={<FaPlus />} size="sm" bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} onClick={create.onOpen}>Novo</Button>
                </Flex>
              </Flex>
              <TabPanels w="100%" minW="0">
                <TabPanel px="0" pt="22px" minW="0"><GeneralTable clients={filtered} onOpen={openDetail} /></TabPanel>
                <TabPanel px="0" pt="22px" minW="0"><Pipeline groups={grouped} onOpen={openDetail} onStatus={moveStatus} /></TabPanel>
                <TabPanel px="0" pt="22px" minW="0"><FollowUpCalendar clients={filtered} onOpen={openDetail} /></TabPanel>
              </TabPanels>
            </Tabs>
            {loading && <Flex py="70px" justify="center"><Spinner thickness="3px" size="xl" /></Flex>}
          </Box>
        </Box>
      </Layout>

      <Drawer isOpen={detail.isOpen} placement="right" onClose={requestClose} size="full">
        <DrawerOverlay />
        <DrawerContent bg={crm.surface} color={crm.text} w={{ base: "100%", md: "620px" }} maxW="100%" ml="auto">
          <DrawerCloseButton />
          <DrawerHeader borderBottom="1px solid" borderColor={crm.border} pr="55px">
            <Text fontSize="xs" color={crm.textMuted} mb="6px">CADASTRO COMERCIAL</Text>
            <Heading size="lg">{selected?.name}</Heading>
            <Flex gap="18px" mt="18px"><Metric label="Honorário" value={percent(selected?.honorariumPercent)} /><Metric label="Margem" value={percent(selected?.negotiatedMarginPercent)} /><Metric label="Segmento" value={selected?.segment ?? "Vazio"} /></Flex>
          </DrawerHeader>
          <DrawerBody pb="32px">
            {selected && <>
              <Box mt="22px"><Text fontSize="sm" color={crm.textMuted} mb="8px">Propriedades</Text>
                <Box mb="10px"><Field label="Nome do cliente"><Input value={draft.name ?? ""} maxLength={200} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></Field>{(draft.name ?? "").trim() !== (selected.name ?? "") && <Text fontSize="xs" color={crm.warning} mt="4px">Renomeia o cliente em todo o sistema (processos, configurações, portal do cliente).</Text>}</Box>
                <SimpleGrid columns={2} spacing="10px"><Field label="Status"><Select value={draft.status ?? "Prospect"} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>{commercialStatuses.map((item) => <option value={item} key={item}>{item}</option>)}</Select></Field><Field label="CNPJ"><Input value={draft.cnpj ?? selected.cnpj ?? ""} isReadOnly /></Field><Field label="Honorário (%)"><Input type="number" step="0.01" value={draft.honorariumPercent ?? ""} onChange={(e) => setDraft({ ...draft, honorariumPercent: e.target.value === "" ? null : Number(e.target.value) })} /></Field><Field label="Margem (%)"><Input type="number" step="0.01" value={draft.negotiatedMarginPercent ?? ""} onChange={(e) => setDraft({ ...draft, negotiatedMarginPercent: e.target.value === "" ? null : Number(e.target.value) })} /></Field><Field label="Segmento"><Input value={draft.segment ?? ""} onChange={(e) => setDraft({ ...draft, segment: e.target.value })} /></Field><Field label="Analista"><Input value={draft.analyst ?? ""} onChange={(e) => setDraft({ ...draft, analyst: e.target.value })} /></Field><Field label="Responsável"><Input value={draft.responsible ?? ""} onChange={(e) => setDraft({ ...draft, responsible: e.target.value })} /></Field><Field label="Comercial responsável"><Input value={draft.commercialResponsible ?? ""} onChange={(e) => setDraft({ ...draft, commercialResponsible: e.target.value })} /></Field><Field label="Agente 1 (comissionado)"><Input value={draft.agent1 ?? ""} onChange={(e) => setDraft({ ...draft, agent1: e.target.value })} /></Field><Field label="Agente 2 (comissionado)"><Input value={draft.agent2 ?? ""} onChange={(e) => setDraft({ ...draft, agent2: e.target.value })} /></Field><Field label="Empresa (cadastro)"><Input value={companyTypeLabel(selected.companyType)} isReadOnly /></Field><Field label="Operação (cadastro)"><Input value={selected.companyOperation ?? ""} isReadOnly /></Field><Field label="Comercial manutenção"><Input value={draft.commercialMaintenance ?? ""} onChange={(e) => setDraft({ ...draft, commercialMaintenance: e.target.value })} /></Field><Field label="Despachante"><Input value={draft.dispatcher ?? ""} onChange={(e) => setDraft({ ...draft, dispatcher: e.target.value })} /></Field><Field label="Radar"><Input value={draft.radar ?? ""} onChange={(e) => setDraft({ ...draft, radar: e.target.value })} /></Field><Field label="Contrato"><Input value={draft.contract ?? ""} onChange={(e) => setDraft({ ...draft, contract: e.target.value })} /></Field><Field label="Nº dossiê"><Input value={draft.dossierNumber ?? ""} onChange={(e) => setDraft({ ...draft, dossierNumber: e.target.value })} /></Field><Field label="Averbação seguro"><Input value={draft.insuranceEndorsement ?? ""} onChange={(e) => setDraft({ ...draft, insuranceEndorsement: e.target.value })} /></Field><Field label="Frete internacional"><Input value={draft.internationalFreight ?? ""} onChange={(e) => setDraft({ ...draft, internationalFreight: e.target.value })} /></Field><Field label="Cidade"><Input value={draft.city ?? ""} onChange={(e) => setDraft({ ...draft, city: e.target.value })} /></Field><Field label="UF"><Input value={draft.state ?? ""} onChange={(e) => setDraft({ ...draft, state: e.target.value })} /></Field><Field label="Contato"><Input value={draft.contactName ?? ""} onChange={(e) => setDraft({ ...draft, contactName: e.target.value })} /></Field><Field label="Telefone"><Input value={draft.phone ?? ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></Field><Field label="Email"><Input value={draft.email ?? ""} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></Field><Field label="ABC"><Input value={draft.abc ?? ""} onChange={(e) => setDraft({ ...draft, abc: e.target.value })} /></Field><Field label="Data de acompanhamento"><Input type="date" value={dateInput(draft.followUpDate)} onChange={(e) => setDraft({ ...draft, followUpDate: e.target.value || null })} /></Field><Field label="FOB 12 meses"><Input value={draft.fob12Months ?? ""} onChange={(e) => setDraft({ ...draft, fob12Months: e.target.value })} /></Field></SimpleGrid>
                <Field label="Observações / condições negociadas"><Textarea rows={4} value={draft.notes ?? ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></Field>
                <BranchesEditor branches={draft.branches ?? []} onChange={(branches) => setDraft({ ...draft, branches })} />
                <Button mt="18px" colorScheme="blue" onClick={saveDetail}>Salvar alterações</Button>
                {selected.id && <ClientBillingSettings commercialClientId={selected.id} />}
              </Box>
              {selected.id && <Box mt="35px" borderTop="1px solid" borderColor={crm.border} pt="20px"><ProceduresPanel scope={{ commercialClientId: selected.id }} onDirtyChange={setProceduresDirty} emptyText="Nenhum procedimento operacional. Também aparece na ficha da Intranet." /></Box>}
              <Box mt="35px" borderTop="1px solid" borderColor={crm.border} pt="20px"><Flex align="center" gap="8px"><FaComment /><Heading size="sm">Comentários</Heading></Flex>{selected.comments?.map((item) => <Box key={item.id} mt="16px"><Text fontSize="sm" fontWeight="700">{item.authorName ?? "Equipe comercial"} <Text as="span" fontWeight="400" color={crm.textMuted}>{dateText(item.createdDate)}</Text></Text><Text mt="4px" fontSize="sm" whiteSpace="pre-wrap">{item.text}</Text></Box>)}<Textarea mt="18px" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Registrar condição negociada ou observação..." /><Button mt="8px" size="sm" onClick={saveComment} colorScheme="blue">Adicionar comentário</Button></Box>
            </>}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <AlertDialog isOpen={confirmDiscard.isOpen} leastDestructiveRef={keepEditingRef} onClose={confirmDiscard.onClose} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px" boxShadow={crm.shadowLg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Descartar alterações?</AlertDialogHeader>
            <AlertDialogBody>Há alterações não salvas {discardTarget === "create" ? "neste novo cadastro" : "neste cadastro"}. Se fechar agora, elas serão perdidas.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={keepEditingRef} variant="ghost" onClick={confirmDiscard.onClose}>Continuar editando</Button>
              <Button colorScheme="red" ml={3} onClick={discardChanges}>Descartar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Modal isOpen={create.isOpen} onClose={requestCloseCreate} size="lg"><ModalOverlay /><ModalContent><ModalHeader>Novo cliente ativo</ModalHeader><ModalCloseButton /><ModalBody><SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px"><Field label="Cliente"><Input value={newClient.newCompanyName ?? ""} isReadOnly={Boolean(newClient.companyId)} onChange={(e) => setNewClient({ ...newClient, newCompanyName: e.target.value })} /></Field><Field label="CNPJ"><Input value={newClient.newCompanyCnpj ?? ""} isReadOnly={Boolean(newClient.companyId)} onChange={(e) => setNewClient({ ...newClient, newCompanyCnpj: e.target.value })} /></Field><Field label="Status"><Select value={newClient.status ?? "Prospect"} onChange={(e) => setNewClient({ ...newClient, status: e.target.value })}>{commercialStatuses.map((item) => <option value={item} key={item}>{item}</option>)}</Select></Field><Field label="Honorário (%)"><Input type="number" step="0.01" onChange={(e) => setNewClient({ ...newClient, honorariumPercent: e.target.value === "" ? null : Number(e.target.value) })} /></Field><Field label="Margem (%)"><Input type="number" step="0.01" onChange={(e) => setNewClient({ ...newClient, negotiatedMarginPercent: e.target.value === "" ? null : Number(e.target.value) })} /></Field><Field label="Responsável"><Input onChange={(e) => setNewClient({ ...newClient, responsible: e.target.value })} /></Field></SimpleGrid></ModalBody><ModalFooter><Button variant="ghost" mr={3} onClick={requestCloseCreate}>Cancelar</Button><Button colorScheme="blue" onClick={createClient}>Criar cadastro</Button></ModalFooter></ModalContent></Modal>
    </Box>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => <FormControl><FormLabel color="gray.500" fontSize="xs" mb="4px">{label}</FormLabel>{children}</FormControl>;
const Metric = ({ label, value }: { label: string; value: string }) => <Box><Text color={crm.textMuted} fontSize="xs">{label}</Text><Text fontSize="sm" mt="3px">{value}</Text></Box>;

const GeneralTable = ({ clients, onOpen }: { clients: CommercialClient[]; onOpen: (client: CommercialClient) => void }) => (
  <Box w="100%" maxW="100%" minW="0" overflowX="auto" border="1px solid" borderColor={crm.border} borderRadius={crm.radius} bg={crm.surface} boxShadow={crm.shadowSm}>
    <Box as="table" minW="1600px" w="100%" sx={{
      borderCollapse: "collapse",
      "th, td": { borderBottom: "1px solid", borderColor: crm.border, px: "12px", py: "10px", textAlign: "left", fontSize: "sm" },
      th: { color: crm.textMuted, fontWeight: "600", whiteSpace: "nowrap", bg: crm.subtle },
      "tbody tr:hover": { bg: crm.brandSoft },
    }}>
      <thead><tr>{["Cliente", "Analista", "Responsável", "Agente 1", "Agente 2", "Empresa", "Operação", "Honorário", "Margem", "CNPJ", "Despachante", "Segmento", "Radar", "Contrato", "Nº dossiê", "Seguro", "Frete", "Cidade", "UF"].map((header) => <th key={header}>{header}</th>)}</tr></thead>
      <tbody>{clients.map((client) => <tr key={client.id}>
        <td style={{ position: "sticky", left: 0, background: crm.surface, minWidth: 230 }}><Button variant="link" color={crm.brand} fontWeight="700" onClick={() => onOpen(client)}>{client.name}</Button>{(client.comments?.length ?? 0) > 0 && <Text as="span" color={crm.textMuted} ml="8px">◌ {client.comments?.length}</Text>}</td>
        <td>{client.analyst ?? ""}</td><td>{client.responsible ?? ""}</td><td>{client.agent1 ?? ""}</td><td>{client.agent2 ?? ""}</td><td>{companyTypeLabel(client.companyType)}</td><td>{client.companyOperation ?? ""}</td><td>{percent(client.honorariumPercent)}</td><td>{percent(client.negotiatedMarginPercent)}</td><td>{client.cnpj ?? ""}</td><td>{client.dispatcher ?? ""}</td><td>{client.segment ?? ""}</td><td>{client.radar ?? ""}</td><td>{client.contract && <Badge colorScheme={client.contract === "Assinado" ? "green" : "gray"}>{client.contract}</Badge>}</td><td>{client.dossierNumber ?? ""}</td><td>{client.insuranceEndorsement && <Badge colorScheme={client.insuranceEndorsement === "SIM" ? "green" : "orange"}>{client.insuranceEndorsement}</Badge>}</td><td>{client.internationalFreight ?? ""}</td><td>{client.city ?? ""}</td><td>{client.state ?? ""}</td>
      </tr>)}</tbody>
    </Box>
    {clients.length === 0 && <Text p="30px" color={crm.textMuted} textAlign="center">Nenhum cadastro encontrado.</Text>}
  </Box>
);

const Pipeline = ({ groups, onOpen, onStatus }: { groups: { name: string; clients: CommercialClient[] }[]; onOpen: (client: CommercialClient) => void; onStatus: (client: CommercialClient, status: string) => void }) => (
  <Flex gap="12px" overflowX="auto" pb="12px">
    {groups.map((group) => <Box key={group.name} minW="260px" w="260px" bg={crm.columnBg} border="1px solid" borderColor={crm.border} borderRadius={crm.radius} p="10px">
      <Badge colorScheme={statusColors[group.name] ?? "gray"} borderRadius="full" px="8px">{group.name}</Badge>
      <Text as="span" color={crm.textMuted} fontSize="xs" ml="7px">{group.clients.length}</Text>
      <Box mt="10px">{group.clients.map((client) => <Box key={client.id} bg={crm.surface} border="1px solid" borderColor={crm.border} boxShadow={crm.shadowSm} borderRadius="9px" p="13px" mb="8px" cursor="pointer" _hover={{ borderColor: crm.brand }} onClick={() => onOpen(client)}>
        <Text fontWeight="700" fontSize="sm" color={crm.text}>▰ {client.name}</Text>
        <Text fontSize="xs" color={crm.textMuted} mt="8px">{percent(client.honorariumPercent)}</Text>
        {(client.comments?.length ?? 0) > 0 && <Text color={crm.textMuted} fontSize="xs" mt="5px">◌ {client.comments?.length}</Text>}
        <Select mt="9px" size="xs" bg={crm.surface} borderColor={crm.borderStrong} value={client.status} onClick={(e) => e.stopPropagation()} onChange={(e) => onStatus(client, e.target.value)}>{commercialStatuses.map((status) => <option key={status} value={status}>{status}</option>)}</Select>
      </Box>)}</Box>
    </Box>)}
  </Flex>
);

const FollowUpCalendar = ({ clients, onOpen }: { clients: CommercialClient[]; onOpen: (client: CommercialClient) => void }) => (
  <MonthCalendar
    items={clients}
    getDate={(client) => client.followUpDate}
    getKey={(client) => client.id!}
    getLabel={(client) => client.name ?? ""}
    colorScheme={(client) => statusColors[client.status ?? ""] ?? "gray"}
    onOpen={onOpen}
  />
);

export default CommercialClients;
